import React, { useState, useEffect } from 'react';
import { auth } from '../../lib/api';

export default function AuthModal({ onClose, onAuthed, initialMode = "login", initialReferralCode = "" }) {
  const [mode, setMode] = useState(initialMode); // "login", "signup", or "admin"
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [registrationCode, setRegistrationCode] = useState("");
  const [referralCode, setReferralCode] = useState(initialReferralCode.trim().toUpperCase());
  const [role, setRole] = useState("STUDENT");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [codeValidation, setCodeValidation] = useState({ valid: false, message: "" });

  useEffect(() => {
    if (initialReferralCode) {
      setReferralCode(initialReferralCode.trim().toUpperCase());
    }
  }, [initialReferralCode]);

  // Validate registration code in real-time
  useEffect(() => {
    if (registrationCode.length >= 3) {
      validateCode(registrationCode);
    } else {
      setCodeValidation({ valid: false, message: "" });
    }
  }, [registrationCode]);

  const validateCode = async (code) => {
    try {
      const data = await auth.validateCode(code);

      if (data && data.role) {
        setCodeValidation({
          valid: true,
          message: `Valid ${data.role} code`
        });
        setRole(data.role);
      } else {
        setCodeValidation({
          valid: false,
          message: "Invalid code response"
        });
      }
    } catch (error) {
      console.error("Validation error:", error);
      setCodeValidation({
        valid: false,
        message: "Invalid or expired code"
      });
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Validate code before submission
    if (mode === "signup" && !codeValidation.valid) {
      setError("Please enter a valid registration code");
      setLoading(false);
      return;
    }

    try {
      if (mode === "signup") {
        console.log("Signup request submitted", { email, registrationCode, referralCode });
        const response = await auth.signup(firstName, lastName, phone, email, password, registrationCode, referralCode);
        setMode("login");
        setSuccess(response?.message || "Account created successfully.");
        return;
      }

      const response = await auth.login(email, password);
      const { user: userData, accessToken } = response || {};

      if (!userData || !accessToken) {
        throw new Error("Invalid login response from server");
      }

      if (mode === "admin" && userData.role !== "ADMIN") {
        throw new Error("Access denied. Admin privileges required.");
      }

      onAuthed({ user: userData, token: accessToken });
    } catch (err) {
      console.error("Signup error:", err);
      setError(err.message || "Error");
      setSuccess("");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/40 pt-20">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl mx-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {mode === "login" ? "Login" : mode === "signup" ? "Sign Up" : "Admin Login"}
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          {mode === "signup" && (
            <>
              <div>
                <label className="block text-sm font-medium">First Name</label>
                <input
                  className="mt-1 w-full rounded-xl border px-3 py-2"
                  type="text"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Last Name</label>
                <input
                  className="mt-1 w-full rounded-xl border px-3 py-2"
                  type="text"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium">Phone Number</label>
                <input
                  className="mt-1 w-full rounded-xl border px-3 py-2"
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  required
                  placeholder="e.g. 347-471-8104"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium">Email</label>
            <input
              className="mt-1 w-full rounded-xl border px-3 py-2"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Password</label>
            <input
              className="mt-1 w-full rounded-xl border px-3 py-2"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>

          {mode === "signup" && (
            <>
              <div>
                <label className="block text-sm font-medium">Registration Code</label>
                <div className="relative">
                  <input
                    className={`mt-1 w-full rounded-xl border px-3 py-2 pr-10 ${codeValidation.valid
                      ? 'border-green-500 bg-green-50'
                      : codeValidation.message && !codeValidation.valid
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    type="text"
                    placeholder="Enter your registration code"
                    value={registrationCode}
                    onChange={e => setRegistrationCode(e.target.value)}
                    required
                  />
                  {codeValidation.message && (
                    <div className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-sm ${codeValidation.valid ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {codeValidation.valid ? '✓' : '✗'}
                    </div>
                  )}
                </div>
                {codeValidation.message && (
                  <p className={`text-xs mt-1 ${codeValidation.valid ? 'text-green-600' : 'text-red-600'
                    }`}>
                    {codeValidation.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium">Referral Code <span className="font-normal text-slate-400">(optional)</span></label>
                <input
                  className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 uppercase"
                  type="text"
                  placeholder="REF-XXXX"
                  value={referralCode}
                  onChange={e => setReferralCode(e.target.value.toUpperCase())}
                />
                {referralCode && (
                  <p className="mt-1 text-xs text-blue-600">Referral code added from your invitation link.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium">Role</label>
                <select
                  className="mt-1 w-full rounded-xl border px-3 py-2"
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  disabled
                >
                  <option value="STUDENT">Student</option>
                  <option value="INSTRUCTOR">Instructor</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Role is automatically assigned based on your registration code
                </p>
              </div>
            </>
          )}

          {success && (
            <div className="text-green-600 text-sm bg-green-50 p-2 rounded-lg">
              {success}
            </div>
          )}
          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-2 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-red-600 px-4 py-2 text-white font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? "Loading..." : mode === "login" ? "Login" : mode === "signup" ? "Sign Up" : "Admin Login"}
          </button>
        </form>

        <div className="mt-4 text-center">
          {mode === "login" ? (
            <button
              onClick={() => setMode("signup")}
              className="block w-full rounded-xl border border-red-600 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              New Student
            </button>
          ) : mode === "signup" ? (
            <button
              onClick={() => setMode("login")}
              className="text-sm text-red-600 hover:text-red-700"
            >
              Already have an account? Login
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
